import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';

import type { SaveCustomPacingMutation, SaveCustomPacingMutationVariables } from '../../../../../apis/graphql';
import { AlertType } from '../../../../../common/Alert';
import { openToastAlert } from '../../../../../common/ToastAlert/toastAlert';
import { CUSTOM_PACING_WAS_SAVED_SUCCESSFULLY_MESSAGE, DEFAULT_CUSTOM_PACING_SAVE_ERROR } from '../constants';
import { useCustomPacingScheduleContext } from '../CustomPacingScheduleProvider/CustomPacingScheduleProvider';
import { SAVE_CUSTOM_PACING } from '../mutations';
import { CpScheduleFormFields, Step } from '../types';
import { useRefetchUpdatedLineItems } from './useRefetchUpdatedLineItems';
import { useScheduleFormState } from './useScheduleFormState';
import { constructSaveCustomPacingInput } from './utils';

type UseSaveCustomPacing = {
  handleSaveCustomPacing: () => Promise<void>;
  isLoading: boolean;
};

export const useSaveCustomPacing = (): UseSaveCustomPacing => {
  const { refetchLineItems, isLoading: isRefetching } = useRefetchUpdatedLineItems();
  const { values } = useScheduleFormState();
  const { setSavedScheduleResult, goToStep } = useCustomPacingScheduleContext();

  const [saveCustomPacing, { loading }] = useMutation<SaveCustomPacingMutation, SaveCustomPacingMutationVariables>(
    SAVE_CUSTOM_PACING
  );

  const handleSaveCustomPacing = async (): Promise<void> => {
    const lineItemList = values[CpScheduleFormFields.LineItemList];
    const ranges = values[CpScheduleFormFields.Ranges];
    const timezone = values[CpScheduleFormFields.Timezone];
    const isDynamicStartDateEnabled = values[CpScheduleFormFields.IsDynamicStartDateEnabled];
    const isDynamicEndDateEnabled = values[CpScheduleFormFields.IsDynamicEndDateEnabled];

    const { data, errors } = await saveCustomPacing({
      variables: {
        input: constructSaveCustomPacingInput(
          lineItemList,
          ranges,
          timezone,
          isDynamicStartDateEnabled,
          isDynamicEndDateEnabled
        ),
      },
    });

    if (data) {
      try {
        await refetchLineItems(data.saveCustomPacing.lineItemIdList);

        setSavedScheduleResult({
          ...data.saveCustomPacing,
          errors: [...data.saveCustomPacing.errors, ...(values[CpScheduleFormFields.ErrorList] || [])],
        });
        goToStep(Step.Result);

        openToastAlert({
          alertType: AlertType.SUCCESS,
          message: CUSTOM_PACING_WAS_SAVED_SUCCESSFULLY_MESSAGE,
        });
      } catch (e) {
        const error = e as ApolloError;

        openToastAlert({
          alertType: AlertType.ERROR,
          message: error.message,
        });
      }
    }

    if (errors) {
      const errorMessage = errors[0]?.message ?? DEFAULT_CUSTOM_PACING_SAVE_ERROR;

      openToastAlert({
        alertType: AlertType.ERROR,
        message: errorMessage,
      });
    }
  };

  return {
    handleSaveCustomPacing,
    isLoading: loading || isRefetching,
  };
};
